import { matchPath } from 'react-router';
import * as ROUTES from '../../../routes';

const CANONICAL_HOST = 'app.small-improvements.com';
const ROOT_DOMAIN = 'small-improvements.com';
const ROOT_DOMAIN_SUFFIX = `.${ROOT_DOMAIN}`;
const URL_PROPERTIES = ['$current_url', '$initial_current_url', '$referrer', '$initial_referrer'] as const;
const PATHNAME_PROPERTIES = ['$pathname', '$initial_pathname'] as const;
const APP_BASE_PATH = ROUTES.BASE;

type UrlPropertyKey = (typeof URL_PROPERTIES)[number];
type PathnamePropertyKey = (typeof PATHNAME_PROPERTIES)[number];
type SanitizableProperties = Record<string, unknown> & {
    $host?: unknown;
} & Partial<Record<UrlPropertyKey | PathnamePropertyKey, unknown>>;

const ALL_ROUTE_PATHS: readonly string[] = Object.values(ROUTES);

// Routes with no ":param" are matched by plain string equality, so they never touch path-to-regexp
// and always win over a same-shaped dynamic route (e.g. "/meetings/templates" vs "/meetings/:userIds").
const STATIC_ROUTE_PATHS: ReadonlySet<string> = new Set(ALL_ROUTE_PATHS.filter(path => !path.includes(':')));

// Tie-break for the rare case where two dynamic routes could shape-match the same pathname:
// prefer more segments, then more static characters, then fewer dynamic segments.
function routeSpecificity(routePath: string): number {
    const segments = routePath.split('/').filter(Boolean);
    let dynamicSegmentCount = 0;
    let staticCharacterCount = 0;

    for (const segment of segments) {
        if (segment.startsWith(':')) {
            dynamicSegmentCount += 1;
        } else {
            staticCharacterCount += segment.length;
        }
    }

    return segments.length * 100 + staticCharacterCount * 10 - dynamicSegmentCount;
}

const DYNAMIC_ROUTE_PATHS: readonly string[] = ALL_ROUTE_PATHS.filter(path => path.includes(':')).sort(
    (left, right) => routeSpecificity(right) - routeSpecificity(left)
);

// Finds the route definition react-router itself would match a given pathname against, so we never
// have to guess (via param naming heuristics) which dynamic segments are safe to report to PostHog.
function matchRouteTemplate(path: string): string | null {
    const normalizedPath = path !== '/' ? path.replace(/\/$/, '') : path;
    if (STATIC_ROUTE_PATHS.has(normalizedPath)) {
        return normalizedPath;
    }

    for (const routePath of DYNAMIC_ROUTE_PATHS) {
        if (matchPath(path, { path: routePath, exact: true })) {
            // Strip the "?" optional-param marker (e.g. ":companyId?") - it's not valid in a URL
            // pathname, and both cases already collapse to this one template.
            return routePath.replace(/\?/g, '');
        }
    }

    return null;
}

function belongsToSmallImprovements(hostname: string): boolean {
    return hostname === ROOT_DOMAIN || hostname.endsWith(ROOT_DOMAIN_SUFFIX);
}

function normalizeHostname(hostname: string): string {
    return belongsToSmallImprovements(hostname) ? CANONICAL_HOST : hostname;
}

function normalizePathname(pathname: string): string {
    const hasAppBasePrefix = pathname === APP_BASE_PATH || pathname.startsWith(`${APP_BASE_PATH}/`);
    const path = hasAppBasePrefix ? pathname.slice(APP_BASE_PATH.length) || '/' : pathname;

    const routeTemplate = matchRouteTemplate(path);
    if (!routeTemplate) {
        return pathname;
    }

    return hasAppBasePrefix ? `${APP_BASE_PATH}${routeTemplate}` : routeTemplate;
}

function tryParseAbsoluteUrl(urlStr: string): URL | null {
    try {
        const url = new URL(urlStr);
        return url.protocol === 'http:' || url.protocol === 'https:' ? url : null;
    } catch {
        return null;
    }
}

export function normalizeHost(host: string): string {
    const parsed = tryParseAbsoluteUrl(`https://${host}`);
    if (!parsed) {
        return host;
    }

    const normalizedHostname = normalizeHostname(parsed.hostname);
    if (normalizedHostname === parsed.hostname) {
        return host;
    }

    return parsed.port ? `${normalizedHostname}:${parsed.port}` : normalizedHostname;
}

export function normalizeUrl(urlStr: string): string {
    const parsed = tryParseAbsoluteUrl(urlStr);
    if (!parsed) {
        return urlStr;
    }

    const shouldNormalizePathname = belongsToSmallImprovements(parsed.hostname);
    const normalizedHostname = normalizeHostname(parsed.hostname);
    const normalizedPathname = shouldNormalizePathname ? normalizePathname(parsed.pathname) : parsed.pathname;

    if (normalizedHostname === parsed.hostname && normalizedPathname === parsed.pathname) {
        return urlStr;
    }

    parsed.hostname = normalizedHostname;
    parsed.pathname = normalizedPathname;
    return parsed.toString();
}

export function sanitizePostHogProperties<T extends SanitizableProperties>(properties: T): T {
    if (!properties || typeof properties !== 'object') {
        return properties;
    }

    const sanitized: T = { ...properties };

    if (typeof sanitized.$host === 'string') {
        sanitized.$host = normalizeHost(sanitized.$host);
    }

    for (const key of URL_PROPERTIES) {
        if (typeof sanitized[key] === 'string') {
            sanitized[key] = normalizeUrl(sanitized[key]);
        }
    }

    for (const key of PATHNAME_PROPERTIES) {
        if (typeof sanitized[key] === 'string') {
            sanitized[key] = normalizePathname(sanitized[key]);
        }
    }

    return sanitized;
}
